/**
 * useLabelPreviews
 *
 * Server-rendered label previews for a print dialog (SV-551): one preview per
 * label that will print, each drawn by <ZplLabelPreview> from the ZPL the
 * preview endpoint renders through the print path for the selected printer.
 *
 * - load(requests) asks for one preview per { labelType, data }. Build `data`
 *   with the same function the dialog passes to submitPrintJob, so the preview
 *   and the print can't drift.
 * - At most 4 requests run at once (useLatestRequestQueue); a newer load or a
 *   printer change aborts the old batch.
 * - When the selected printer changes while the dialog is open, the last
 *   requests are rebuilt and re-fetched after a 300 ms debounce.
 * - A failed preview only marks that label with an error; printing never
 *   depends on it.
 *
 * Usage:
 *   const { previews, load } = useLabelPreviews({ selectedPrinter, visible: () => props.visible });
 *   await load(() => labels.map((l) => ({ labelType: 'MILK_POOL_LABEL', data: buildPrintData(l) })));
 *   <ZplLabelPreview v-for="p in previews" v-bind="previewProps(p)" />
 */
import { getCurrentInstance, onBeforeUnmount, ref, watch, type Ref } from 'vue';
import { previewLabel, type LabelPreviewEmptyField } from '@/shared/services/print-jobs.api';
import { useLatestRequestQueue } from './useLatestRequestQueue';
import { useLocale } from './useLocale';

export interface LabelPreviewRequest {
  labelType: string;
  data: Record<string, unknown>;
}

export interface LabelPreviewState {
  loading: boolean;
  error: string | null;
  zpl: string;
  dpi: number;
  widthMm: number;
  heightMm: number;
  dpiSource: 'device' | 'fallback' | null;
  missingFields: string[];
  /** Barcode fields that rendered empty; named in the preview's no-data notes. */
  emptyFields: LabelPreviewEmptyField[];
}

/** Preview requests allowed in flight at once (one label each). */
export const PREVIEW_CONCURRENCY = 4;
/** Delay before re-fetching after the printer changes. */
export const PRINTER_CHANGE_DEBOUNCE_MS = 300;

/** Defaults until the response says otherwise (the seeded 2.25" × 1.5" label). */
function emptyPreview(): LabelPreviewState {
  return { loading: true, error: null, zpl: '', dpi: 203, widthMm: 57.15, heightMm: 38.1, dpiSource: null, missingFields: [], emptyFields: [] };
}

/** Props for <ZplLabelPreview v-bind="previewProps(state)">. */
export function previewProps(state: LabelPreviewState) {
  return {
    zpl: state.zpl,
    widthMm: state.widthMm,
    heightMm: state.heightMm,
    dpi: state.dpi,
    dpiSource: state.dpiSource,
    loading: state.loading,
    error: state.error,
    missingFields: state.missingFields,
    emptyFields: state.emptyFields,
  };
}

export function useLabelPreviews(options: {
  selectedPrinter: Ref<{ value: string } | null | undefined>;
  /** Re-fetch on printer change only while this is true (the dialog is open). */
  visible: () => boolean;
}) {
  const { t } = useLocale();
  const previews = ref<LabelPreviewState[]>([]);
  const queue = useLatestRequestQueue({ concurrency: PREVIEW_CONCURRENCY });
  let lastRequests: (() => LabelPreviewRequest[] | null) | null = null;
  /** Printer the current previews were requested for. */
  let previewedDeviceId: string | undefined;
  let printerChangeTimer: ReturnType<typeof setTimeout> | undefined;

  function currentDeviceId(): string | undefined {
    return options.selectedPrinter.value?.value || undefined;
  }

  /**
   * Fetch one preview per request. `requests` is called now and again on each
   * printer change, so pass a function that builds them from current state.
   * Returning null (nothing to preview) clears the previews.
   */
  async function load(requests: () => LabelPreviewRequest[] | null): Promise<void> {
    lastRequests = requests;
    const list = requests();
    if (!list) {
      cancel();
      previews.value = [];
      return;
    }
    const deviceId = currentDeviceId();
    previewedDeviceId = deviceId;
    // Queued labels keep their loading state until their own response arrives.
    previews.value = list.map(emptyPreview);
    await queue.run(
      list,
      (req, _index, signal) => previewLabel(req.labelType, req.data, undefined, deviceId, { signal }),
      (index, outcome) => {
        previews.value[index] = outcome.ok
          ? {
            loading: false,
            error: null,
            zpl: outcome.value.rendered_zpl,
            dpi: outcome.value.dpi ?? 203,
            widthMm: outcome.value.media_width_mm ?? 57.15,
            heightMm: outcome.value.media_height_mm ?? 38.1,
            dpiSource: outcome.value.dpi_source ?? null,
            missingFields: outcome.value.missing_fields ?? [],
            emptyFields: outcome.value.empty_fields ?? [],
          }
          : { ...emptyPreview(), loading: false, error: t('ui.zplLabelPreview.renderError') };
      },
    );
  }

  /** Abort in-flight previews and stop a pending printer-change re-fetch. */
  function cancel(): void {
    clearTimeout(printerChangeTimer);
    queue.cancel();
  }

  // Re-fetch when the user picks another printer (its DPI can pick another template).
  watch(
    () => options.selectedPrinter.value?.value,
    () => {
      clearTimeout(printerChangeTimer);
      if (!options.visible() || !lastRequests) return;
      printerChangeTimer = setTimeout(() => {
        if (lastRequests && currentDeviceId() !== previewedDeviceId) void load(lastRequests);
      }, PRINTER_CHANGE_DEBOUNCE_MS);
    },
  );
  if (getCurrentInstance()) onBeforeUnmount(() => clearTimeout(printerChangeTimer));

  return { previews, load, cancel };
}
